import React, { useState, useEffect } from 'react';
import { X, Check, FileSpreadsheet, Info } from 'lucide-react';

interface CsvMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  rawData: any[][];
  headers: string[];
  onConfirm: (mappedData: { index: string, prompt: string }[]) => void;
}

const CsvMappingModal: React.FC<CsvMappingModalProps> = ({ isOpen, onClose, rawData, headers, onConfirm }) => {
  const [hasHeaders, setHasHeaders] = useState(true);
  const [indexCol, setIndexCol] = useState<number | 'auto'>('auto');
  const [promptCol, setPromptCol] = useState<number>(0);

  // Auto-detect prompt and index columns if they match typical names
  useEffect(() => {
    if (isOpen && headers.length > 0) {
        const pIdx = headers.findIndex(h => h.toLowerCase().includes('prompt') || h.toLowerCase().includes('提示词') || h.toLowerCase().includes('content'));
        const iIdx = headers.findIndex(h => h.toLowerCase() === 'index' || h.toLowerCase() === 'id' || h.toLowerCase() === '序号' || h.toLowerCase() === '编号');
        
        if (pIdx !== -1) setPromptCol(pIdx);
        if (iIdx !== -1) setIndexCol(iIdx);
        else setIndexCol('auto');
    }
  }, [isOpen, headers]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const startIndex = hasHeaders ? 1 : 0;
    const mapped = rawData.slice(startIndex).map((row, i) => {
      const indexValue = indexCol === 'auto' ? String(i + 1) : String(row[indexCol] || (i + 1));
      const promptValue = String(row[promptCol] || '');
      return { index: indexValue, prompt: promptValue };
    }).filter(item => item.prompt.trim().length > 0);

    onConfirm(mapped);
    onClose();
  };

  const previewRows = rawData.slice(0, 5);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-900">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="text-indigo-400" size={20} />
            <h3 className="text-lg font-bold text-white">确认导入提示词</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${hasHeaders ? 'bg-indigo-600 border-indigo-500' : 'bg-slate-950 border-slate-700 group-hover:border-slate-500'}`}>
                  {hasHeaders && <Check size={14} className="text-white" />}
                </div>
                <input type="checkbox" className="hidden" checked={hasHeaders} onChange={() => setHasHeaders(!hasHeaders)} />
                <span className="text-sm font-medium text-slate-200">首行为表头 (Header)</span>
              </label>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase">提示词所在列 (Prompt Column)</label>
                <select 
                  value={promptCol} 
                  onChange={e => setPromptCol(parseInt(e.target.value))}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-sm text-slate-200 outline-none focus:border-indigo-500"
                >
                  {headers.map((h, i) => (
                    <option key={i} value={i}>{hasHeaders ? h : `Column ${i + 1}`}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase">序号所在列 (Index Column)</label>
                <select 
                  value={indexCol} 
                  onChange={e => setIndexCol(e.target.value === 'auto' ? 'auto' : parseInt(e.target.value))}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-sm text-slate-200 outline-none focus:border-indigo-500"
                >
                  <option value="auto">自动生成 (1, 2, 3...)</option>
                  {headers.map((h, i) => (
                    <option key={i} value={i}>{hasHeaders ? h : `Column ${i + 1}`}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 space-y-3">
               <div className="flex items-center gap-2 text-indigo-400">
                  <Info size={16} />
                  <span className="text-sm font-bold">导入说明</span>
               </div>
               <ul className="text-xs text-slate-400 space-y-2 leading-relaxed">
                  <li>• 提示词列不能为空，空行将被自动过滤。</li>
                  <li>• 如果没有专门的序号列，选择“自动生成”。</li>
                  <li>• 支持多列 CSV，您只需指定核心的提示词字段。</li>
                  <li>• 系统会自动尝试检测中英文“提示词”或“Prompt”表头。</li>
               </ul>
            </div>
          </div>

          {/* Preview Table */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold text-slate-500 uppercase px-1">数据预览 (Preview)</h4>
            <div className="border border-slate-800 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-xs text-left border-collapse">
                <thead className="bg-slate-800 text-slate-400">
                  <tr>
                    {headers.map((h, i) => (
                      <th key={i} className={`p-2 border-r border-slate-700 font-medium ${hasHeaders ? '' : 'italic opacity-50'} ${promptCol === i ? 'text-indigo-400 bg-indigo-500/5' : ''} ${indexCol === i ? 'text-emerald-400 bg-emerald-500/5' : ''}`}>
                        {hasHeaders ? h : `Col ${i+1}`}
                        {promptCol === i && <span className="ml-1 text-[8px] bg-indigo-500 text-white px-1 rounded">P</span>}
                        {indexCol === i && <span className="ml-1 text-[8px] bg-emerald-500 text-white px-1 rounded">I</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-slate-950/50">
                  {previewRows.map((row, ri) => (
                    <tr key={ri} className={`border-t border-slate-800 ${ri === 0 && hasHeaders ? 'opacity-30 bg-slate-900' : ''}`}>
                      {headers.map((_, ci) => (
                        <td key={ci} className={`p-2 border-r border-slate-700 truncate max-w-[150px] text-slate-300 ${promptCol === ci ? 'bg-indigo-500/5' : ''} ${indexCol === ci ? 'bg-emerald-500/5' : ''}`}>
                          {String(row[ci] || '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-900/50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">取消</button>
          <button 
            onClick={handleConfirm}
            className="px-8 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-bold shadow-lg shadow-indigo-900/20 transition-all active:scale-95"
          >
            确认并导入 ({rawData.length - (hasHeaders ? 1 : 0)} 条)
          </button>
        </div>
      </div>
    </div>
  );
};

export default CsvMappingModal;